import { useState } from 'react';
import { Copy, CheckCircle, Code, Book, Shield, Terminal, Webhook } from 'lucide-react';

function CodeBlock({ code, lang = 'javascript' }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="relative bg-slate-900 border border-slate-700 rounded-xl overflow-hidden">
      <button onClick={copy}
        className="absolute top-3 right-3 text-slate-500 hover:text-white transition-colors">
        {copied ? <CheckCircle className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
      </button>
      <pre className="text-sm text-slate-300 p-4 overflow-x-auto leading-relaxed">
        <code>{code}</code>
      </pre>
    </div>
  );
}

function Section({ id, icon: Icon, title, children }) {
  return (
    <section id={id} className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-xl bg-cyan-500/10 border border-cyan-500/20">
          <Icon className="w-5 h-5 text-cyan-400" />
        </div>
        <h2 className="text-white font-bold text-lg">{title}</h2>
      </div>
      {children}
    </section>
  );
}

const ERRORS = [
  { code: '401', name: 'Unauthorized', desc: 'Invalid or missing API key' },
  { code: '403', name: 'Forbidden',    desc: 'Merchant account suspended' },
  { code: '400', name: 'Bad Request',  desc: 'Invalid parameters — see errors array' },
  { code: '404', name: 'Not Found',    desc: 'Charge not found' },
  { code: '410', name: 'Gone',         desc: 'Charge expired' },
  { code: '503', name: 'Unavailable',  desc: 'API temporarily disabled' },
];

export default function MerchantApiDocs() {
  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Top nav */}
      <div className="border-b border-slate-800 sticky top-0 bg-slate-950/95 backdrop-blur z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <div className="p-1.5 rounded-lg bg-cyan-500/20">
            <Code className="w-5 h-5 text-cyan-400" />
          </div>
          <span className="font-bold text-cyan-400">DWI Pay</span>
          <span className="text-slate-500 text-sm font-medium">API v1 Docs</span>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-10 space-y-12">

        {/* Hero */}
        <div className="space-y-3">
          <h1 className="text-3xl font-black text-white">Merchant Payments API</h1>
          <p className="text-slate-400 text-lg max-w-2xl">
            Accept UGX payments via DWIPay Wallet or Mobile Money. Create a charge, share the hosted
            payment page, receive a webhook on success.
          </p>
          <div className="flex flex-wrap gap-2 text-xs font-mono">
            <span className="px-3 py-1 rounded-full bg-slate-800 text-slate-300 border border-slate-700">Base URL: https://dwifintech.com/api/v1/merchant</span>
            <span className="px-3 py-1 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30">UGX only (v1)</span>
          </div>
        </div>

        {/* Authentication */}
        <Section id="auth" icon={Shield} title="Authentication">
          <p className="text-slate-400 text-sm">Pass your secret key in the <code className="text-cyan-400 bg-slate-800 px-1 rounded">Authorization</code> header.
          Get your keys from <strong className="text-white">Merchant → API Keys</strong>.</p>
          <CodeBlock lang="http" code={`curl -X POST https://dwifintech.com/api/v1/merchant/charges \\
  -H "Authorization: Bearer sk_live_YOUR_SECRET_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"amount": 50000, "description": "Order #1234"}'`} />
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 space-y-2 text-sm">
            <p className="text-slate-300 font-semibold">Key types</p>
            <div className="space-y-1">
              <div className="flex gap-3"><code className="text-amber-300 text-xs font-mono w-28">sk_test_...</code><span className="text-slate-400 text-xs">Test mode — simulates payment, no real funds</span></div>
              <div className="flex gap-3"><code className="text-emerald-300 text-xs font-mono w-28">sk_live_...</code><span className="text-slate-400 text-xs">Live mode — real UGX payments</span></div>
            </div>
          </div>
        </Section>

        {/* Create Charge */}
        <Section id="create-charge" icon={Terminal} title="Create a Charge">
          <div className="flex gap-2 items-center text-sm">
            <span className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 px-2 py-0.5 rounded font-mono font-bold text-xs">POST</span>
            <code className="text-slate-300 font-mono text-xs">/api/v1/merchant/charges</code>
          </div>
          <CodeBlock code={`// Request
{
  "amount":       50000,          // UGX, minimum 500
  "description":  "Order #1234",  // shown to customer
  "customerPhone":"0701234567",   // optional, pre-fills momo form
  "redirectUrl":  "https://yoursite.com/thanks",  // optional
  "expireMinutes": 30,            // optional, default 30
  "metadata":     { "orderId": "1234" }  // optional
}

// Response 201
{
  "success":   true,
  "chargeId":  "ch_abc123...",
  "payUrl":    "https://dwifintech.com/pay/c/ch_abc123...",
  "amount":    50000,
  "currency":  "UGX",
  "status":    "pending",
  "expiresAt": "2026-07-08T15:30:00.000Z",
  "mode":      "live"
}`} />
          <p className="text-slate-400 text-sm">Share <code className="text-cyan-400 bg-slate-800 px-1 rounded">payUrl</code> with your customer — they choose DWIPay Wallet or Mobile Money on the hosted page.</p>
        </Section>

        {/* Check Status */}
        <Section id="check-status" icon={Book} title="Check Charge Status">
          <div className="flex gap-2 items-center text-sm">
            <span className="bg-blue-500/20 text-blue-300 border border-blue-500/30 px-2 py-0.5 rounded font-mono font-bold text-xs">GET</span>
            <code className="text-slate-300 font-mono text-xs">/api/v1/merchant/charges/:chargeId</code>
          </div>
          <CodeBlock code={`// Response
{
  "success": true,
  "charge": {
    "chargeId":    "ch_abc123...",
    "amount":      50000,
    "status":      "succeeded",  // pending | succeeded | failed | expired
    "paymentRail": "momo",       // wallet | momo
    "feeAmount":   1250,
    "netAmount":   48750,
    "createdAt":   "2026-07-08T14:00:00.000Z"
  }
}`} />
        </Section>

        {/* List Charges */}
        <Section id="list-charges" icon={Book} title="List Charges">
          <div className="flex gap-2 items-center text-sm">
            <span className="bg-blue-500/20 text-blue-300 border border-blue-500/30 px-2 py-0.5 rounded font-mono font-bold text-xs">GET</span>
            <code className="text-slate-300 font-mono text-xs">/api/v1/merchant/charges?status=succeeded&page=1&limit=20</code>
          </div>
          <p className="text-slate-400 text-sm">Query params: <code className="text-cyan-400 text-xs">status</code>, <code className="text-cyan-400 text-xs">from</code>, <code className="text-cyan-400 text-xs">to</code> (ISO dates), <code className="text-cyan-400 text-xs">page</code>, <code className="text-cyan-400 text-xs">limit</code> (max 100).</p>
        </Section>

        {/* Webhooks */}
        <Section id="webhooks" icon={Webhook} title="Webhooks">
          <p className="text-slate-400 text-sm">Configure a webhook endpoint in <strong className="text-white">Merchant → Webhooks</strong>.
          We sign every delivery with HMAC-SHA256 using your signing secret.</p>
          <div className="grid gap-3">
            {['charge.succeeded','charge.failed','charge.expired'].map(e => (
              <div key={e} className="flex items-center gap-2 text-sm bg-slate-800 border border-slate-700 rounded-xl px-4 py-2">
                <span className="w-2 h-2 rounded-full bg-cyan-400 shrink-0" />
                <code className="text-cyan-300 font-mono text-xs">{e}</code>
              </div>
            ))}
          </div>
          <p className="text-slate-400 text-sm mt-2">Verify the signature in <code className="text-cyan-400 text-xs">X-DWI-Signature</code> header:</p>
          <CodeBlock code={`// Node.js — verify DWI webhook signature
const crypto = require('crypto');

function verifyDwiWebhook(rawBody, signature, signingSecret) {
  const expected = 'sha256=' + crypto
    .createHmac('sha256', signingSecret)
    .update(rawBody)        // raw Buffer — do NOT parse JSON first
    .digest('hex');
  return crypto.timingSafeEqual(
    Buffer.from(expected),
    Buffer.from(signature)
  );
}

// Express example
app.post('/webhook/dwi', express.raw({ type: 'application/json' }), (req, res) => {
  const sig    = req.headers['x-dwi-signature'];
  const secret = process.env.DWI_WEBHOOK_SECRET;

  if (!verifyDwiWebhook(req.body, sig, secret)) {
    return res.status(400).send('Bad signature');
  }

  const event = JSON.parse(req.body);
  if (event.event === 'charge.succeeded') {
    // fulfil order
    console.log('Paid:', event.chargeId, event.netAmount);
  }
  res.status(200).send('OK');
});`} />
        </Section>

        {/* Test Mode */}
        <Section id="test-mode" icon={Terminal} title="Test Mode">
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-sm space-y-2">
            <p className="text-amber-300 font-semibold">Test mode behaviour</p>
            <ul className="text-slate-400 space-y-1 list-disc list-inside">
              <li>Use <code className="text-amber-300 text-xs">sk_test_...</code> keys — no real money moves</li>
              <li>Wallet payments succeed instantly if the payer has a test balance</li>
              <li>Mobile money prompts go to the Yo! sandbox (test SIM required)</li>
              <li>Charges show a "TEST MODE" badge on the payment page</li>
              <li>Webhooks are delivered normally so you can test your integration</li>
            </ul>
          </div>
        </Section>

        {/* Errors */}
        <Section id="errors" icon={Shield} title="Error Codes">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-slate-700">
                  <th className="pb-2 text-slate-400 font-medium w-16">Code</th>
                  <th className="pb-2 text-slate-400 font-medium w-32">Name</th>
                  <th className="pb-2 text-slate-400 font-medium">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {ERRORS.map(e => (
                  <tr key={e.code}>
                    <td className="py-2.5 font-mono text-red-400 text-xs">{e.code}</td>
                    <td className="py-2.5 text-white text-xs">{e.name}</td>
                    <td className="py-2.5 text-slate-400 text-xs">{e.desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

      </div>
    </div>
  );
}
